import {array, boolean, number, object, string} from 'yup';

import {paginationV8nSchema} from '@shared/api';
import {
  localizedTextOptionalNullable,
  localizedTextRequired,
  numberOptionalNullable,
  stringOptionalNullable,
} from '@shared/utils';
import {
  EventAuditActor,
  EventDisplayType,
  EventResult,
  EventStatus,
  ParticipantLogoOption,
} from '../types';

export const groupEventV8nSchema = object().shape({
  id: string().required(),
  event_id: stringOptionalNullable,
  slug: stringOptionalNullable,
  name: localizedTextRequired,
  short_name: localizedTextOptionalNullable,
  description: localizedTextOptionalNullable,
  logo_url: stringOptionalNullable,
  short_logo_url: stringOptionalNullable,
  group_order: numberOptionalNullable,
  group_type: numberOptionalNullable,
  trading_volume: numberOptionalNullable,
  yes_price: numberOptionalNullable,
  no_price: numberOptionalNullable,
});
export const topicV8nSchema = object().shape({
  id: string().required(),
  topic_id: stringOptionalNullable,
  topic_slug: stringOptionalNullable,
  slug: stringOptionalNullable,
  name: localizedTextRequired,
  logo_url: stringOptionalNullable,
});

const eventParticipantV8nSchema = object().shape({
  id: string().required(),
  name: stringOptionalNullable,
  logo_url: stringOptionalNullable,
  logo_url_stroke: stringOptionalNullable,
  logo_url_player: stringOptionalNullable,
  logo_url_custom: stringOptionalNullable,
  nation: stringOptionalNullable,
});

export const eventLogV8nSchema = object().shape({
  id: string().required(),
  action: string().required(),
  reason: string().required(),
  timestamp: string().required(),
  actor_type: string().oneOf(Object.values(EventAuditActor)).required(),
  actor_id: string().nullable().defined(),
  status_before: string()
    .oneOf(Object.values(EventStatus))
    .nullable()
    .defined(),
  status_after: string().oneOf(Object.values(EventStatus)).nullable().defined(),
  result_before: string()
    .oneOf(Object.values(EventResult))
    .nullable()
    .defined(),
  result_after: string().oneOf(Object.values(EventResult)).nullable().defined(),
});

export const v8nSchemaOfGetEventLogsResponse = paginationV8nSchema.shape({
  array: array().of(eventLogV8nSchema).required(),
});

const eventSubCategoryV8nSchema = object()
  .shape({
    id: stringOptionalNullable,
    slug: stringOptionalNullable,
    name: localizedTextRequired,
  })
  .test({
    name: 'event-sub-category-id',
    message: 'Either id or slug should be present',
    test(value) {
      return Boolean(value?.id || value?.slug);
    },
  });

export const eventListItemV8nSchema = object()
  .shape({
    id: string().required(),
    event_id: stringOptionalNullable,
    slug: stringOptionalNullable,
    name: localizedTextRequired,
    short_name: localizedTextOptionalNullable,
    description: localizedTextOptionalNullable,
    category_name: localizedTextOptionalNullable,
    sub_categories: array().of(eventSubCategoryV8nSchema).nullable().optional(),
    subcategories: array().of(eventSubCategoryV8nSchema).nullable().optional(),
    geo_slugs: array().of(string().required()).nullable().optional(),
    start_date: stringOptionalNullable,
    end_date: stringOptionalNullable,
    live_date: stringOptionalNullable,
    is_favorite: boolean().nullable().optional(),
    logo_url: stringOptionalNullable,
    yes_price: numberOptionalNullable,
    no_price: numberOptionalNullable,
    topic: topicV8nSchema.nullable().optional(),
    is_live: boolean().nullable().optional(),
    auto_publish_enabled: boolean().required(),
    is_promoted: boolean().nullable().optional(),
    yes_text: localizedTextOptionalNullable,
    no_text: localizedTextOptionalNullable,
    trading_volume: numberOptionalNullable,
    status: string().oneOf(Object.values(EventStatus)).nullable().optional(),
    result: string().oneOf(Object.values(EventResult)).nullable().optional(),
    group: array().of(groupEventV8nSchema).nullable().optional(),
    groups: array().of(groupEventV8nSchema).nullable().optional(),
    group_id: stringOptionalNullable,
    holders: number().nullable().optional(),
    participants: number().nullable().optional(),
    short_logo_url: stringOptionalNullable,
    group_order: numberOptionalNullable,
    group_type: numberOptionalNullable,
    tabs: object()
      .shape({
        overview: localizedTextOptionalNullable,
        source_of_truth: localizedTextOptionalNullable,
        rules: localizedTextOptionalNullable,
      })
      .nullable()
      .optional(),
    display_type: string()
      .oneOf(Object.values(EventDisplayType))
      .nullable()
      .optional(),
    participant_logo_option: string()
      .oneOf(Object.values(ParticipantLogoOption))
      .nullable()
      .optional(),
    participant_short_logo_option: string()
      .oneOf(Object.values(ParticipantLogoOption))
      .nullable()
      .optional(),
  })
  .test({
    name: 'event-list-item-id',
    message: 'Either event_id or slug should be present',
    test(value) {
      return Boolean(value?.event_id || value?.slug);
    },
  });

const tabsV8nSchema = object()
  .shape({
    overview: localizedTextOptionalNullable,
    source_of_truth: localizedTextOptionalNullable,
    rules: localizedTextOptionalNullable,
  })
  .nullable()
  .optional();

export const eventV8nSchema = object()
  .shape({
    id: string().required(),
    event_id: stringOptionalNullable,
    slug: stringOptionalNullable,
    name: localizedTextRequired,
    short_name: localizedTextOptionalNullable,
    description: localizedTextOptionalNullable,
    category_name: localizedTextOptionalNullable,
    sub_categories: array().of(eventSubCategoryV8nSchema).nullable().optional(),
    subcategories: array().of(eventSubCategoryV8nSchema).nullable().optional(),
    geo_slugs: array().of(string().required()).nullable().optional(),
    start_date: string().required(),
    end_date: string().required(),
    live_date: stringOptionalNullable,
    is_favorite: boolean().nullable().optional(),
    logo_url: stringOptionalNullable,
    short_logo_url: stringOptionalNullable,
    yes_price: number(),
    no_price: number(),
    topic: topicV8nSchema.nullable().optional(),
    is_live: boolean().required(),
    auto_publish_enabled: boolean().required(),
    is_promoted: boolean().nullable().optional(),
    yes_text: localizedTextOptionalNullable,
    no_text: localizedTextOptionalNullable,
    trading_volume: number().integer().required(),
    status: string().oneOf(Object.values(EventStatus)).required(),
    result: string().oneOf(Object.values(EventResult)).required(),
    group: array().of(groupEventV8nSchema).nullable().optional(),
    groups: array().of(groupEventV8nSchema).nullable().optional(),
    group_id: stringOptionalNullable,
    holders: number().nullable().optional(),
    participants: number().nullable().optional(),
    tabs: tabsV8nSchema,
    display_type: string()
      .oneOf(Object.values(EventDisplayType))
      .nullable()
      .optional(),
    participant_logo_option: string()
      .oneOf(Object.values(ParticipantLogoOption))
      .nullable()
      .optional(),
    participant_short_logo_option: string()
      .oneOf(Object.values(ParticipantLogoOption))
      .nullable()
      .optional(),
    participant_id: stringOptionalNullable,
    external_participant_id: stringOptionalNullable,
    yes_participant_id: stringOptionalNullable,
    external_yes_participant_id: stringOptionalNullable,
    no_participant_id: stringOptionalNullable,
    external_no_participant_id: stringOptionalNullable,
    participant: eventParticipantV8nSchema.nullable().optional(),
    yes_participant: eventParticipantV8nSchema.nullable().optional(),
    no_participant: eventParticipantV8nSchema.nullable().optional(),
  })
  .test({
    name: 'event-id',
    message: 'Either event_id or slug should be present',
    test(value) {
      return Boolean(value?.event_id || value?.slug);
    },
  });

export const v8nSchemaOfGetEventsResponse = paginationV8nSchema.shape({
  array: array().of(eventListItemV8nSchema),
});
